import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Save, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { useFormShapeGuard } from '@/hooks/useFormShapeGuard';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function ModifierClient({ isOpen, onClose, client, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [formData, setFormData] = useState({
    nom: '',
    type_client: 'residentiel',
    telephone: '',
    email: '',
    adresse_client: '',
    ville_client: '',
    code_postal_client: '',
    notes_generales: '',
    representant_id: '',
    representant_nom: '',
    contact_nom: '',
    contact_telephone: '',
    contact_email: '',
    date_creation: '',
    est_test: false,
  });

  // 🛡️ Filet de sécurité : voir /app/memory/PRD.md « Conventions de code »
  useFormShapeGuard('ModifierClient', formData);

  useEffect(() => {
    if (client) {
      setFormData({
        nom: client.nom || '',
        type_client: client.type_client || 'residentiel',
        telephone: client.telephone || '',
        email: client.email || '',
        adresse_client: client.adresse_client || '',
        ville_client: client.ville_client || '',
        code_postal_client: client.code_postal_client || '',
        notes_generales: client.notes_generales || '',
        representant_id: client.representant_id || '',
        representant_nom: client.representant_nom || '',
        contact_nom: client.contact_nom || '',
        contact_telephone: client.contact_telephone || '',
        contact_email: client.contact_email || '',
        date_creation: client.date_creation ? client.date_creation.split('T')[0] : '',
        est_test: !!client.est_test,
      });
    }
  }, [client]);

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`);
      if (res.status === 200) {
        const data = res.data;
        setEmployees(data.employees || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (isOpen) fetchEmployees(); }, [isOpen, fetchEmployees]);

  const handleRepChange = (empId) => {
    if (empId === 'aucun') {
      setFormData(prev => ({ ...prev, representant_id: '', representant_nom: '' }));
    } else {
      const emp = employees.find(e => e.id === empId);
      if (emp) setFormData(prev => ({ ...prev, representant_id: empId, representant_nom: `${emp.prenom} ${emp.nom}` }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom || !formData.telephone) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      const dateCreation = formData.date_creation && !formData.date_creation.includes('T')
        ? new Date(formData.date_creation + 'T12:00:00').toISOString()
        : (formData.date_creation || client.date_creation);
      const payload = { ...client, ...formData, id: client.id, actif: true, date_creation: dateCreation };
      const response = await axios.put(`${API_URL}/api/crm/clients/${client.id}`, payload);
      if (response.status !== 200) throw new Error('Erreur');
      toast.success('Client modifié avec succès!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier le Client</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Infos de base */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nom du Client *</Label>
                  <Input value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} required />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={formData.type_client} onValueChange={(val) => setFormData({ ...formData, type_client: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residentiel">Résidentiel</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Téléphone *</Label>
                  <Input placeholder="(514) 555-1234" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: formatPhoneNumber(e.target.value) })} required />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client depuis</Label>
                  <Input type="date" value={formData.date_creation} onChange={(e) => setFormData({ ...formData, date_creation: e.target.value })} data-testid="client-date-creation" />
                </div>
              </div>
            </div>

            {/* Contact commercial */}
            {formData.type_client === 'commercial' && (
              <div className="border-t pt-4 space-y-4">
                <h3 className="font-semibold">Contact dans l'entreprise</h3>
                <div>
                  <Label>Nom du contact</Label>
                  <Input value={formData.contact_nom} onChange={(e) => setFormData({ ...formData, contact_nom: e.target.value })} placeholder="Nom de la personne-ressource" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Téléphone du contact</Label>
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => setFormData({ ...formData, contact_telephone: formData.telephone })}>
                        <Copy className="w-3 h-3 mr-1" /> Même que l'entreprise
                      </Button>
                    </div>
                    <Input value={formData.contact_telephone} onChange={(e) => setFormData({ ...formData, contact_telephone: formatPhoneNumber(e.target.value) })} placeholder="(514) 555-1234" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label>Courriel du contact</Label>
                      <Button type="button" variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => setFormData({ ...formData, contact_email: formData.email })}>
                        <Copy className="w-3 h-3 mr-1" /> Même que l'entreprise
                      </Button>
                    </div>
                    <Input type="email" value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} placeholder="contact@entreprise.com" />
                  </div>
                </div>
              </div>
            )}

            {/* Adresse */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold">Adresse du Client</h3>
              <div>
                <Label>Adresse *</Label>
                <Input value={formData.adresse_client} onChange={(e) => setFormData({ ...formData, adresse_client: e.target.value })} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ville *</Label>
                  <Input value={formData.ville_client} onChange={(e) => setFormData({ ...formData, ville_client: e.target.value })} required />
                </div>
                <div>
                  <Label>Code Postal *</Label>
                  <Input value={formData.code_postal_client} onChange={(e) => setFormData({ ...formData, code_postal_client: e.target.value })} required />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <Label>Notes Générales</Label>
              <Textarea value={formData.notes_generales} onChange={(e) => setFormData({ ...formData, notes_generales: e.target.value })} rows={3} />
            </div>

            {/* 🧪 Indicateur "Client test" — auto-détecté si le nom correspond à un employé */}
            {formData.est_test && (
              <div className="border-t pt-4">
                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm" data-testid="indicateur-client-test">
                  <p className="font-semibold text-amber-900 mb-0.5">🧪 Client de test</p>
                  <p className="text-xs text-amber-700">
                    Ce client est automatiquement classé comme "test" parce que son nom correspond
                    à celui d'un employé actif. Il reste visible dans la page <strong>Clients</strong>
                    mais est exclu de <strong>Tous les projets</strong>, <strong>Production à venir</strong>
                    et des statistiques. Aucune action requise.
                  </p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
              <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
